import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { isIOS } from "react-device-detect";

import { useModalContext } from "@/shared/contexts/modal";
import { ArrowIcon, Checkbox, Typography } from "@/components/index";
import { selectorMainTreeSgBooksMap } from "@/shared/store/mainTree/selectors";
import { swActions } from "@/store/actions";
import { selectorBookBaseBible } from "@/shared/store/selectors";
import Accordion from "@/components/Accordion/Accordion";
import { getOptionKey, makeOption } from "@/shared/utils/select";
import { useBookWithSWFallback } from "@/shared/hooks/pwa/sw";
import ModalActions from "@/shared/components/modal/ModalActions";
import Button from "@/shared/components/Button/Button";
import IconView from "@/shared/components/IconView/IconView";
import EgwWebFont from "../../../assets/EgwWebFont";
import {
  postSWMessageRemoveAudio,
  postSWMessageRemoveBook, postSWMessageRemoveWaveform
} from "@/shared/utils/sw/sw";
import { msToTimeLabel } from "@/shared/utils/time";
import { bytesToMb } from "@/shared/utils/file";
import { toggleInArray } from "@/shared/utils/array";
import {
  filterTOCToAudioChapters,
  getFileDuration,
  getFileSize
} from "@/shared/utils/player";
import { I18N_NAMESPACE } from "@/shared/utils/multilang/constants";

import styles from "./ModalDownload.module.scss";
import { selectorAudiosDownloadedIds } from "@/shared/store/sw/selectorAudiosDownloadedIds";
import { selectorDownloadedBooksIds } from "@/shared/store/sw/selectorDownloadedBooksIds";

const BookCover = dynamic(() => import("@/shared/components/BookCoverView"), {
  ssr: false
});

const BookItem = ({
  book, toc,
  isChecked, isDownloaded,
  onChangeChecked, onRemoveBook, onRemoveAudio,
  audioChaptersIds, setAudioChaptersIds
}) => {
  const { t } = useTranslation();

  const downloadedAudiosIds = useSelector(selectorAudiosDownloadedIds);

  const [audioChaptersOptions, mapChapterIdToChapter] = useMemo(() => {
    const audioChapters = filterTOCToAudioChapters(toc);

    const map = {};

    if (audioChapters) {
      for (const audioChapter of audioChapters) {
        map[audioChapter.para_id] = audioChapter;
      }
    }

    return [
      audioChapters.map((c) => makeOption(c.para_id, c.title)),
      map,
    ];
  }, [toc]);

  const downloadedChapterIds = useMemo(() => {
    return Object.keys(mapChapterIdToChapter).filter((id) => downloadedAudiosIds.includes(id));
  }, [mapChapterIdToChapter, downloadedAudiosIds]);

  return (
    <div className={styles["book-item"]}>
      <div className={styles["book-item_wrap"]}>
        <div>
          {
            isDownloaded ? (
              <Button onClick={() => onRemoveBook(book)}>
                <IconView icon={EgwWebFont.delete} />
              </Button>
            ) : <Checkbox checked={isChecked} onChange={onChangeChecked} />
          }
        </div>
        <div className={styles["book-item_book-content"]}>
          <BookCover className={styles["book-item_cover"]} book={book} />
          <div className={styles["book-item_right"]}>
            <Typography
              variant="h5"
              color="inherit"
              fontFamily="roboto"
              dangerouslySetInnerHTML={{ __html: book.title }}
            />
            <Typography
              variant="body3"
              clamp={3}
              dangerouslySetInnerHTML={{ __html: book.description }}
            />
          </div>
        </div>
      </div>
      {
        !!audioChaptersOptions.length && (
          <Accordion
            head={
              <>
                <Typography className={styles["accordion-head_mp3"]} hoverable variant="h4">
                  {t("mp3")}
                  <ArrowIcon className={styles["arrow-icon"]} />
                  &nbsp;({downloadedChapterIds.length}/{audioChaptersOptions.length})
                </Typography>
              </>
            }
            body={
              <table className={styles["table-mp3"]}>
                <thead>
                  <tr>
                    <th align="left">{t("chapter")}</th>
                    <th align="left">{t("duration")}</th>
                    <th align="right">{t("size")}(mb)</th>
                  </tr>
                </thead>
                <tbody>
                  {audioChaptersOptions.map((item) => {
                    const key = getOptionKey(item);
                    const audioChapter = mapChapterIdToChapter[key];
                    const checkbox = (
                      <Checkbox
                        key={key}
                        title={item.label}
                        checked={audioChaptersIds.includes(key)}
                        onChange={() => {
                          const valueNew = [...audioChaptersIds];
                          toggleInArray(valueNew, key);
                          setAudioChaptersIds(valueNew);
                        }}
                      />
                    );

                    let content;
                    if (downloadedChapterIds.includes(key)) {
                      content = (
                        <div className={styles["audio-chapter_actions"]}>
                          <Button onClick={() => onRemoveAudio(audioChapter)}>
                            <IconView icon={EgwWebFont.delete} />
                          </Button>
                          <Typography
                            // threeDots
                            variant="body3"
                            className={styles["label"]}
                            color="inherit"
                          >{item.label}</Typography>
                        </div>
                      );
                    } else {
                      content = checkbox;
                    }

                    return (
                      <tr className={styles["audio-chapter-item"]} key={key}>
                        <td align="left">
                          {content}
                        </td>
                        <td align="left">
                          {msToTimeLabel(getFileDuration(audioChapter.mp3) * 1000, ":")}
                        </td>
                        <td align="right">
                          {bytesToMb(getFileSize(audioChapter.mp3))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            }
          />
        )
      }
    </div>
  );
};

const ViewDownloadBook = ({
  bookId, onClickDownload, onClickRemove, onClose, onRemoveAudio
}) => {
  const { t } = useTranslation();

  const { book, toc } = useBookWithSWFallback(bookId);
  const downloadedBooksIds = useSelector(selectorDownloadedBooksIds);
  const bookBaseBible = useSelector(selectorBookBaseBible);

  const [audioChaptersIds, setAudioChaptersIds] = useState([]);
  const [isDownloadBook, setIsDownloadBook] = useState(true);
  const [isDownloadSG, setIsDownloadSG] = useState(true);
  const [isDownloadBible, setIsDownloadBible] = useState(false);

  const sgBooksMap = useSelector(selectorMainTreeSgBooksMap);
  const sgBook = book ? sgBooksMap[book.code] : null;

  if (!book) {
    return null;
  }

  return (
    <div className={styles["modal_body"]}>
      <BookItem
        isDownloaded={downloadedBooksIds.includes(book.book_id.toString())}
        book={book}
        toc={toc}
        isChecked={isDownloadBook}
        onChangeChecked={setIsDownloadBook}
        audioChaptersIds={audioChaptersIds}
        setAudioChaptersIds={setAudioChaptersIds}
        onRemoveBook={onClickRemove}
        onRemoveAudio={onRemoveAudio}
      />
      <div>
        {isIOS && downloadedBooksIds.length > 9 && <Typography
          tag="div"
          className={styles["note"]}
          variant={Typography.variant.body4}
          dangerouslySetInnerHTML={{
            __html: t("maxIOSLimit", {
              ns: I18N_NAMESPACE.sw,
              count: downloadedBooksIds.length,
            }),
          }}
        />}
        <hr />
      </div>
      <Typography variant="h5" fontFamily="roboto">
        {t("sw:relatedBooks")}:
      </Typography>
      {
        sgBook && (
          <BookItem
            isDownloaded={downloadedBooksIds.includes(sgBook.book_id.toString())}
            book={sgBook}
            toc={[]}
            isChecked={isDownloadSG}
            onChangeChecked={setIsDownloadSG}
            onRemoveBook={onClickRemove}
            onRemoveAudio={onRemoveAudio}
          />
        )
      }
      {
        bookBaseBible && (
          <BookItem
            isDownloaded={downloadedBooksIds.includes(bookBaseBible.book_id.toString())}
            book={bookBaseBible}
            toc={[]}
            isChecked={isDownloadBible}
            onChangeChecked={setIsDownloadBible}
            onRemoveBook={onClickRemove}
            onRemoveAudio={onRemoveAudio}
          />
        )
      }
      <ModalActions
        className={styles["modal-actions"]}
        onClickNegative={onClose}
        onClickPositive={() => {
          const books = [];
          if (book && isDownloadBook) {
            books.push(book);
          }
          if (sgBook && isDownloadSG) {
            books.push(sgBook);
          }
          if (bookBaseBible && isDownloadBible) {
            books.push(bookBaseBible);
          }
          const audioChapters = toc.filter((c) => audioChaptersIds.includes(c.para_id));
          onClickDownload(books, audioChapters);
        }}
        positiveText={t("download")}
      />
    </div>
  );
};

const ModalDownload = ({ bookId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { onClose, showDialog } = useModalContext();

  const handleDownload = useCallback((books, audioChapters) => {
    if (books.length) {
      dispatch(swActions.downloadDataBookData(books));
    }
    if (audioChapters.length) {
      dispatch(swActions.downloadDataAudio(audioChapters));
    }

    onClose();
  }, [onClose]);

  const handleRemove = useCallback((book) => {
    showDialog(t("confirmation"), t("sw:confirmationRemove", {
      book: book.title ? `"${book.title}"` : t("book"),
    }),
    () => {
      postSWMessageRemoveBook(book.book_id);
      return true;
    });
  }, [t]);

  const handleRemoveAudio = useCallback((chapter) => {
    showDialog(t("confirmation"), t("sw:confirmationRemove", {
      book: `"${chapter.title}"`,
    }),
    () => {
      postSWMessageRemoveAudio(chapter.para_id);
      postSWMessageRemoveWaveform(chapter.para_id);
      return true;
    });
  }, [t]);

  return (
    <ViewDownloadBook
      bookId={bookId}
      onClickDownload={handleDownload}
      onClickRemove={handleRemove}
      onRemoveAudio={handleRemoveAudio}
      onClose={onClose}
    />
  );
};

export const useModalDownload = () => {
  const { t } = useTranslation();

  const { showModal } = useModalContext();

  return useCallback((bookId) => {
    showModal(<ModalDownload bookId={bookId} />, {
      title: t("sw:downloadForOfflineUse"),
      className: styles["modal"],
    });
  }, [t, showModal]);
};
